import { useEffect, useState } from "react";
import {
    Badge, Box, Button, Chip, Collapse, Grid, Paper,
    Stack, TextField, Tooltip, Typography, useTheme
} from "@mui/material";
import FilterListIcon from '@mui/icons-material/FilterList';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import Templete from "../../template/Template";
import { Plus } from "lucide-react";
import Cards from "../../components/Cards/Cards";
import TableData from "../../components/Tables/TableData";
import { formatDate } from "../../utilities/date";
import { displayMoney } from "../../utilities/displayValues";
import { GridColDef } from "@mui/x-data-grid";
import ModalContaReceber from "./ModalContaReceber";
import { isPermite } from "../../utilities/isPermite";
import { ContasReceberType } from "../../types/ContasReceberType";
import { api } from "../../services/api";
import { AlertInfo } from "../../components/Alerts/Alert";
import SimpleBackdrop from "../../components/Loading/SimpleBackdrop";
import moment from "moment";

const MES_INICIO = moment().startOf('month').format('YYYY-MM-DD');
const MES_FIM = moment().endOf('month').format('YYYY-MM-DD');

const STATUS_OPTIONS = [
    { value: '', label: 'Todos' },
    { value: '0', label: 'A Vencer' },
    { value: '1', label: 'Recebido' },
    { value: '2', label: 'Em Atraso' },
    { value: '3', label: 'Cancelado' },
];

export default function ContasReceber() {
    const [openLoad, setOpenLoad] = useState(false);
    const [openModal, setOpenModal] = useState(false);
    const [rows, setRows] = useState<ContasReceberType[]>([]);
    const [contaReceber, setContaReceber] = useState<ContasReceberType | null>(null);
    const [showFiltros, setShowFiltros] = useState(false);
    const theme = useTheme();

    // Estados dos filtros
    const [filtroNome, setFiltroNome] = useState('');
    const [filtroCpf, setFiltroCpf] = useState('');
    const [filtroSituacao, setFiltroSituacao] = useState('');
    const [filtroPeriodo, setFiltroPeriodo] = useState<'DATA' | 'SEMPRE'>('DATA');
    const [filtroDataInicio, setFiltroDataInicio] = useState(MES_INICIO);
    const [filtroDataFim, setFiltroDataFim] = useState(MES_FIM);

    // Conta quantos filtros estão fora do padrão
    const filtrosAtivos = [
        filtroNome.trim() !== '',
        filtroCpf.trim() !== '',
        filtroSituacao !== '',
        filtroPeriodo === 'DATA' && (filtroDataInicio !== MES_INICIO || filtroDataFim !== MES_FIM),
        filtroPeriodo === 'SEMPRE',
    ].filter(Boolean).length;

    const columns: GridColDef[] = [
        { field: 'nome_cliente', headerName: 'CLIENTE', width: 300 },
        { field: 'tipo_conta_receber', headerName: 'TIPO', width: 100 },
        {
            field: 'data_vencimento', headerName: 'VENCIMENTO', width: 130,
            valueFormatter: (dt: any) => formatDate(dt),
        },
        {
            field: 'data_pagamento', headerName: 'PAGAMENTO', width: 130,
            valueFormatter: (dt: any) => formatDate(dt),
        },
        {
            field: 'valor_origem', headerName: 'VALOR', width: 130, align: 'right',
            valueFormatter: (dt: any) => displayMoney(dt),
        },
        {
            field: 'situacao_conta', headerName: 'SITUAÇÃO', width: 130,
            renderCell: (params) => {
                const cod = params.value as number;
                if (cod === 1) return <Chip label="RECEBIDO" color="success" size="small" />;
                if (cod === 2) return <Chip label="EM ATRASO" color="error" size="small" />;
                if (cod === 3) return <Chip label="CANCELADO" size="small" />;
                return <Chip label="A VENCER" color="primary" size="small" />;
            },
        },
        { field: 'status_boleto', headerName: 'STATUS BOLETO', width: 150 },
    ];

    function handleClick(item: ContasReceberType) {
        if (isPermite('73')) {
            setContaReceber(item);
            setOpenModal(true);
        }
    }

    async function loadData(overrides?: {
        nome?: string; cpf?: string; situacao?: string;
        inicio?: string; fim?: string; periodo?: 'DATA' | 'SEMPRE';
    }) {
        setOpenLoad(true);
        try {
            const nome = overrides?.nome ?? filtroNome;
            const cpf = overrides?.cpf ?? filtroCpf;
            const situacao = overrides?.situacao ?? filtroSituacao;
            const periodo = overrides?.periodo ?? filtroPeriodo;
            const inicio = overrides?.inicio ?? filtroDataInicio;
            const fim = overrides?.fim ?? filtroDataFim;

            const body: any = {};
            if (periodo === 'DATA') {
                body.data_inicio = inicio;
                body.data_fim = fim;
            }
            if (nome.trim()) body.nome_cliente = nome.trim();
            if (cpf.trim()) body.cpf_cliente = cpf.trim();
            if (situacao !== '') body.situacao_conta = situacao;

            const response = await api.post('/listcontasreceber', body);
            if (response.data.error) {
                AlertInfo({ icon: 'error', title: 'Falha ao carregar dados' });
            } else {
                const data: ContasReceberType[] = Array.isArray(response.data) ? response.data : [];
                const updated = data.map((item, index) => {
                    let situacao_conta = item.situacao_conta;
                    if (situacao_conta === 0 && moment(item.data_vencimento).isBefore(moment(), 'day')) {
                        situacao_conta = 2;
                    }
                    return { ...item, situacao_conta, id: index };
                });
                setRows(updated);
            }
        } catch {
            AlertInfo({ icon: 'error', title: 'Falha na conexão' });
        }
        setOpenLoad(false);
    }

    function handleLimpar() {
        setFiltroNome('');
        setFiltroCpf('');
        setFiltroSituacao('');
        setFiltroPeriodo('DATA');
        setFiltroDataInicio(MES_INICIO);
        setFiltroDataFim(MES_FIM);
        loadData({ nome: '', cpf: '', situacao: '', periodo: 'DATA', inicio: MES_INICIO, fim: MES_FIM });
    }

    // Cards calculados sobre os dados carregados
    const aVencer = rows.filter(r => r.situacao_conta === 0);
    const emAtraso = rows.filter(r => r.situacao_conta === 2);
    const recebidos = rows.filter(r => r.situacao_conta === 1);
    const totalAVencer = aVencer.reduce((acc, r) => acc + Number(r.valor_origem || 0), 0);
    const totalEmAtraso = emAtraso.reduce((acc, r) => acc + Number(r.valor_origem || 0), 0);
    const totalRecebidos = recebidos.reduce((acc, r) => acc + Number(r.valor_pago || r.valor_origem || 0), 0);

    useEffect(() => { loadData(); }, []);

    return (
        <Templete menuActive="contas_receber">
            <SimpleBackdrop open={openLoad} />
            <ModalContaReceber
                open={openModal}
                setOpen={setOpenModal}
                contaReceber={contaReceber}
                setContaReceber={setContaReceber}
                reload={loadData}
            />

            <Grid container spacing={1}>

                {/* ── Linha de ações ─────────────────────────────────────────── */}
                <Grid item xs={12}>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" justifyContent="space-between" paddingX={2}>
                        <Button
                            variant="contained"
                            startIcon={<Plus size={18} />}
                            onClick={() => { if (isPermite("72")) { setContaReceber(null); setOpenModal(true); } }}
                        >
                            NOVA CONTA
                        </Button>

                        <Box gap={3} display="flex" alignItems="center">
                            <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                                {rows.length} {rows.length === 1 ? 'cobrança' : 'cobranças'} encontradas
                            </Typography>

                            {filtrosAtivos > 0 && (
                                <Tooltip title="Limpar todos os filtros">
                                    <Button
                                        size="small" variant="text" color="inherit"
                                        startIcon={<CloseIcon fontSize="small" />}
                                        onClick={handleLimpar}
                                    >
                                        Limpar ({filtrosAtivos})
                                    </Button>
                                </Tooltip>
                            )}

                            <Badge badgeContent={filtrosAtivos} color="primary" invisible={filtrosAtivos === 0}>
                                <Button
                                    size="small"
                                    variant={showFiltros ? 'contained' : 'outlined'}
                                    startIcon={<FilterListIcon />}
                                    disableElevation
                                    onClick={() => setShowFiltros(v => !v)}
                                >
                                    Filtros
                                </Button>
                            </Badge>
                        </Box>
                    </Stack>
                </Grid>

                {/* ── Painel expansível de filtros ───────────────────────────── */}
                <Grid item xs={12}>
                    <Collapse in={showFiltros}>
                        <Paper variant="outlined" sx={{ p: 2, mb: 1, bgcolor: '#fafafa', borderRadius: 2 }}>
                            <Grid container spacing={2} alignItems="flex-end">

                                {/* Linha 1 */}
                                <Grid item xs={12} sm={5}>
                                    <Typography variant="caption" fontWeight="bold" color="text.secondary" display="block" mb={0.5}>
                                        Nome do Cliente
                                    </Typography>
                                    <TextField
                                        fullWidth size="small"
                                        placeholder="Buscar por nome..."
                                        value={filtroNome}
                                        onChange={e => setFiltroNome(e.target.value)}
                                        onKeyDown={e => e.key === 'Enter' && loadData()}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <Typography variant="caption" fontWeight="bold" color="text.secondary" display="block" mb={0.5}>
                                        CPF do Cliente
                                    </Typography>
                                    <TextField
                                        fullWidth size="small"
                                        placeholder="000.000.000-00"
                                        value={filtroCpf}
                                        onChange={e => setFiltroCpf(e.target.value)}
                                        onKeyDown={e => e.key === 'Enter' && loadData()}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={3}>
                                    <Typography variant="caption" fontWeight="bold" color="text.secondary" display="block" mb={0.5}>
                                        Situação
                                    </Typography>
                                    <TextField
                                        select fullWidth size="small"
                                        value={filtroSituacao}
                                        onChange={e => setFiltroSituacao(e.target.value)}
                                        SelectProps={{ native: true }}
                                    >
                                        {STATUS_OPTIONS.map(o => (
                                            <option key={o.value} value={o.value}>{o.label}</option>
                                        ))}
                                    </TextField>
                                </Grid>

                                {/* Linha 2 */}
                                <Grid item xs={12} sm={2}>
                                    <Typography variant="caption" fontWeight="bold" color="text.secondary" display="block" mb={0.5}>
                                        Período
                                    </Typography>
                                    <TextField
                                        select fullWidth size="small"
                                        value={filtroPeriodo}
                                        onChange={e => setFiltroPeriodo(e.target.value as 'DATA' | 'SEMPRE')}
                                        SelectProps={{ native: true }}
                                    >
                                        <option value="DATA">Por Data</option>
                                        <option value="SEMPRE">Sempre</option>
                                    </TextField>
                                </Grid>
                                <Grid item xs={12} sm={3}>
                                    <Typography variant="caption" fontWeight="bold" color="text.secondary" display="block" mb={0.5}>
                                        Vencimento De
                                    </Typography>
                                    <TextField
                                        fullWidth size="small" type="date"
                                        InputLabelProps={{ shrink: true }}
                                        value={filtroDataInicio}
                                        onChange={e => setFiltroDataInicio(e.target.value)}
                                        disabled={filtroPeriodo === 'SEMPRE'}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={3}>
                                    <Typography variant="caption" fontWeight="bold" color="text.secondary" display="block" mb={0.5}>
                                        Vencimento Até
                                    </Typography>
                                    <TextField
                                        fullWidth size="small" type="date"
                                        InputLabelProps={{ shrink: true }}
                                        value={filtroDataFim}
                                        onChange={e => setFiltroDataFim(e.target.value)}
                                        disabled={filtroPeriodo === 'SEMPRE'}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={2}>
                                    <Button
                                        variant="contained" fullWidth
                                        startIcon={<SearchIcon />}
                                        onClick={() => loadData()}
                                    >
                                        BUSCAR
                                    </Button>
                                </Grid>
                                <Grid item xs={12} sm={2}>
                                    <Button
                                        variant="outlined" fullWidth color="inherit"
                                        startIcon={<CloseIcon />}
                                        onClick={handleLimpar}
                                    >
                                        LIMPAR
                                    </Button>
                                </Grid>

                            </Grid>
                        </Paper>
                    </Collapse>
                </Grid>

                {/* ── Cards de resumo ────────────────────────────────────────── */}
                <Grid item xs={12} sm={4}>
                    <Cards color={theme.palette.primary.main} label="A VENCER"
                        main={String(aVencer.length)} value={displayMoney(totalAVencer)} />
                </Grid>
                <Grid item xs={12} sm={4}>
                    <Cards color={theme.palette.error.main} label="EM ATRASO"
                        main={String(emAtraso.length)} value={displayMoney(totalEmAtraso)} />
                </Grid>
                <Grid item xs={12} sm={4}>
                    <Cards color={theme.palette.success.main} label="RECEBIDOS"
                        main={String(recebidos.length)} value={displayMoney(totalRecebidos)} />
                </Grid>

                {/* ── Tabela ─────────────────────────────────────────────────── */}
                <Grid item xs={12}>
                    <TableData columns={columns} rows={rows} height={480} handleClick={handleClick} />
                </Grid>

            </Grid>
        </Templete>
    );
}
